import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, LogOut, RefreshCw, Check, X, RotateCcw, Phone, Mail, Settings, CalendarDays } from "lucide-react";
import { CONTACT } from "../data/content";
import { PasswordForm, MessagesForm } from "../components/AdminSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cml_admin_token";

const STATUTS = {
    nouvelle: { label: "Nouvelle", cls: "border-neon/40 bg-neon/10 text-neon" },
    confirmee: { label: "Confirmée", cls: "border-emerald-400/40 bg-emerald-400/10 text-emerald-300" },
    annulee: { label: "Annulée", cls: "border-red-400/30 bg-red-400/10 text-red-300" },
};

const dateFr = (d) => {
    try {
        return new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR", {
            weekday: "short",
            day: "numeric",
            month: "short",
        });
    } catch {
        return d;
    }
};

export default function Admin() {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [reservations, setReservations] = useState(null);
    const [view, setView] = useState("reservations");
    const [notice, setNotice] = useState("");

    const fetchReservations = useCallback(
        async (tok = token) => {
            setLoading(true);
            try {
                const res = await fetch(`${API}/admin/reservations`, {
                    headers: { Authorization: `Bearer ${tok}` },
                });
                if (res.status === 401) {
                    localStorage.removeItem(TOKEN_KEY);
                    setToken("");
                    setReservations(null);
                    return;
                }
                if (!res.ok) throw new Error();
                setReservations(await res.json());
            } catch {
                setError("Impossible de charger les réservations.");
            } finally {
                setLoading(false);
            }
        },
        [token],
    );

    useEffect(() => {
        if (token) fetchReservations(token);
    }, [token, fetchReservations]);

    const login = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const res = await fetch(`${API}/admin/login`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ password }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || "Erreur de connexion");
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
            setPassword("");
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const setStatut = async (ref, statut) => {
        try {
            const res = await fetch(`${API}/admin/reservations/${ref}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ statut }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(typeof err.detail === "string" ? err.detail : "Mise à jour impossible.");
            }
            const data = await res.json();
            setReservations((list) =>
                list.map((r) => (r.ref === ref ? { ...r, statut } : r)),
            );
            setError("");
            if (statut === "confirmee" || statut === "annulee") {
                setNotice(
                    data.email_client
                        ? `E-mail de ${statut === "confirmee" ? "confirmation" : "annulation"} envoyé au client (${ref}).`
                        : `Statut mis à jour (${ref}) — pas d'e-mail client renseigné, prévenez-le par téléphone.`,
                );
                setTimeout(() => setNotice(""), 6000);
            }
        } catch {
            setError("Mise à jour impossible.");
        }
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setReservations(null);
    };

    if (!token) {
        return (
            <main className="flex min-h-screen items-center justify-center px-4" data-testid="admin-login-page">
                <motion.form
                    onSubmit={login}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    className="card-line glow-soft w-full max-w-sm rounded-3xl bg-panel p-8"
                >
                    <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-neon/30 bg-neon/5 text-neon">
                        <Lock size={24} />
                    </div>
                    <h1 className="text-center font-display text-xl font-bold text-white">
                        Espace Clean My Life
                    </h1>
                    <p className="mt-1 text-center text-sm text-slate-500">Accès réservé</p>
                    <input
                        type="password"
                        data-testid="admin-password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Mot de passe"
                        className="mt-6 w-full rounded-xl border border-white/10 bg-noir px-4 py-3 text-sm text-white outline-none transition-colors focus:border-neon/60"
                        autoFocus
                    />
                    {error && (
                        <p data-testid="admin-login-error" className="mt-3 text-center text-sm text-red-300">
                            {error}
                        </p>
                    )}
                    <button
                        type="submit"
                        data-testid="admin-login-button"
                        disabled={loading || !password}
                        className="mt-5 w-full rounded-full bg-neon py-3 text-sm font-bold text-noir transition-shadow duration-300 hover:shadow-[0_0_26px_rgba(0,240,255,0.45)] disabled:opacity-50"
                    >
                        {loading ? "Connexion…" : "Se connecter"}
                    </button>
                    <Link to="/" className="mt-5 block text-center text-xs text-slate-600 hover:text-neon">
                        ← Retour au site
                    </Link>
                </motion.form>
            </main>
        );
    }

    return (
        <main className="min-h-screen px-4 pb-24 pt-28 sm:px-6" data-testid="admin-dashboard">
            <div className="mx-auto max-w-4xl">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <p className="font-mono text-xs font-semibold uppercase tracking-[0.35em] text-neon/90">
                            Espace admin
                        </p>
                        <h1 className="mt-1 font-display text-2xl font-extrabold text-white sm:text-3xl">
                            Réservations reçues
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        <button
                            data-testid="admin-refresh-button"
                            onClick={() => fetchReservations()}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:border-neon/40 hover:text-neon"
                        >
                            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Actualiser
                        </button>
                        <button
                            data-testid="admin-logout-button"
                            onClick={logout}
                            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-slate-300 transition-colors hover:border-red-400/50 hover:text-red-300"
                        >
                            <LogOut size={15} /> Quitter
                        </button>
                    </div>
                </div>

                <div className="mt-6 flex gap-2" data-testid="admin-tabs">
                    {[
                        ["reservations", "Réservations", CalendarDays],
                        ["settings", "Paramètres", Settings],
                    ].map(([key, label, Icon]) => (
                        <button
                            key={key}
                            data-testid={`admin-tab-${key}`}
                            onClick={() => setView(key)}
                            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                                view === key
                                    ? "border-neon/60 bg-neon/10 text-neon"
                                    : "border-white/10 text-slate-400 hover:text-white"
                            }`}
                        >
                            <Icon size={14} /> {label}
                        </button>
                    ))}
                </div>

                {error && <p className="mt-4 text-sm text-red-300">{error}</p>}
                {notice && (
                    <p data-testid="admin-notice" className="mt-4 rounded-2xl border border-neon/30 bg-neon/5 px-4 py-3 text-sm text-neon">
                        {notice}
                    </p>
                )}

                {view === "settings" && (
                    <div className="mt-8 space-y-4" data-testid="admin-settings">
                        <PasswordForm
                            token={token}
                            onLogout={logout}
                            onToken={(t) => {
                                localStorage.setItem(TOKEN_KEY, t);
                                setToken(t);
                            }}
                        />
                        <MessagesForm token={token} onLogout={logout} />
                    </div>
                )}

                {view === "reservations" && reservations && reservations.length === 0 && (
                    <p data-testid="admin-empty" className="card-line mt-10 rounded-3xl bg-panel p-10 text-center text-slate-500">
                        Aucune réservation pour le moment.
                    </p>
                )}

                <div className="mt-8 space-y-4">
                    {view === "reservations" && reservations?.map((r) => {
                        const s = STATUTS[r.statut] || STATUTS.nouvelle;
                        return (
                            <article
                                key={r.ref}
                                data-testid={`reservation-card-${r.ref}`}
                                className="card-line rounded-3xl bg-panel p-5 sm:p-6"
                            >
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-sm font-bold tracking-widest text-neon">
                                            {r.ref}
                                        </span>
                                        <span className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${s.cls}`}>
                                            {s.label}
                                        </span>
                                    </div>
                                    <span className="font-display text-base font-bold text-white">
                                        {dateFr(r.date)} · {r.slot}
                                    </span>
                                </div>

                                <div className="mt-4 grid gap-x-8 gap-y-1.5 text-sm text-slate-300 sm:grid-cols-2">
                                    <p>
                                        <span className="text-slate-500">Formule : </span>
                                        {r.formule_label} — {r.prix}
                                    </p>
                                    <p>
                                        <span className="text-slate-500">Véhicule : </span>
                                        {[r.vehicleType, r.marque, r.modele].filter(Boolean).join(" ")}
                                    </p>
                                    <p>
                                        <span className="text-slate-500">Adresse : </span>
                                        {[r.adresse, r.cp, r.ville].filter(Boolean).join(", ")}
                                    </p>
                                    <p>
                                        <span className="text-slate-500">Client : </span>
                                        {r.nom}
                                    </p>
                                </div>
                                {r.infos && (
                                    <p className="mt-1.5 text-sm text-slate-500">Accès : {r.infos}</p>
                                )}

                                <div className="mt-4 flex flex-wrap items-center gap-2">
                                    <a
                                        href={`tel:${r.tel}`}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-xs font-semibold text-slate-200 hover:border-neon/40 hover:text-neon"
                                    >
                                        <Phone size={13} /> {r.tel}
                                    </a>
                                    {r.email && (
                                        <a
                                            href={`mailto:${r.email}`}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-xs font-semibold text-slate-200 hover:border-neon/40 hover:text-neon"
                                        >
                                            <Mail size={13} /> {r.email}
                                        </a>
                                    )}
                                    <span className="grow" />
                                    {r.statut !== "confirmee" && (
                                        <button
                                            data-testid={`status-confirm-${r.ref}`}
                                            onClick={() => setStatut(r.ref, "confirmee")}
                                            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-400/90 px-3.5 py-2 text-xs font-bold text-noir hover:bg-emerald-300"
                                        >
                                            <Check size={13} /> Confirmer
                                        </button>
                                    )}
                                    {r.statut !== "annulee" && (
                                        <button
                                            data-testid={`status-cancel-${r.ref}`}
                                            onClick={() => setStatut(r.ref, "annulee")}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-red-400/40 px-3.5 py-2 text-xs font-semibold text-red-300 hover:bg-red-400/10"
                                        >
                                            <X size={13} /> Annuler
                                        </button>
                                    )}
                                    {r.statut !== "nouvelle" && (
                                        <button
                                            data-testid={`status-reset-${r.ref}`}
                                            onClick={() => setStatut(r.ref, "nouvelle")}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-2 text-xs font-semibold text-slate-400 hover:text-neon"
                                        >
                                            <RotateCcw size={13} /> Nouvelle
                                        </button>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>

                <p className="mt-10 text-center text-xs text-slate-600">
                    Besoin d'aide ? {CONTACT.email}
                </p>
            </div>
        </main>
    );
}
